import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ModalFooter } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import type { useMailboxGuide } from '../hooks/useMailboxGuide';
import { providerOf } from './mailboxGuideState';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';

interface GuideFooterProps {
  guide: ReturnType<typeof useMailboxGuide>;
  onClose: () => void;
}

/**
 * The buttons under each guide card (design outreach-v2 o1 to o7 `g-foot`, p `mp-foot`). Next stays off
 * for a mailbox that is not supported (p4): the guide of such a mailbox does not open.
 */
const GuideFooterComponent: React.FC<GuideFooterProps> = ({ guide, onClose }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const { phase, providerId } = guide;
  const unsupported = providerId !== null && providerOf(providerId).passwordKind === 'unsupported';
  const hasAddress = guide.email.includes('@');

  if (phase === 'checking') {
    return null;
  }
  if (phase === 'connected') {
    return (
      <ModalFooter>
        <SMLButton color="primary" omitCap onClick={onClose}>
          {intl.formatMessage(messages.done)}
        </SMLButton>
      </ModalFooter>
    );
  }
  if (phase === 'email') {
    return (
      <ModalFooter className="justify-content-between">
        <SMLButton color="link" omitCap onClick={onClose}>
          {intl.formatMessage(messages.cancel)}
        </SMLButton>
        <SMLButton
          color="primary"
          omitCap
          disabled={!hasAddress || providerId === null || unsupported}
          onClick={() => guide.setPhase('guide')}
          testID="mailbox-guide-next"
        >
          {intl.formatMessage(messages.next)}
        </SMLButton>
      </ModalFooter>
    );
  }
  return (
    <ModalFooter className="justify-content-between">
      <div className="d-flex gap-2">
        <SMLButton color="link" omitCap onClick={() => guide.setPhase(phase === 'admin' ? 'guide' : 'email')}>
          {intl.formatMessage(messages.back)}
        </SMLButton>
        <SMLButton
          color="link"
          omitCap
          onClick={() => {
            navigator.clipboard
              .writeText(window.location.href)
              .then(() => setCopied(true))
              .catch(console.error);
          }}
        >
          {intl.formatMessage(copied ? messages.linkCopied : messages.askTeammate)}
        </SMLButton>
      </div>
      <SMLButton
        color="primary"
        omitCap
        disabled={!guide.canConnect}
        onClick={guide.connect}
        testID="mailbox-guide-connect"
      >
        {intl.formatMessage(phase === 'admin' ? messages.adminDone : messages.connect)}
      </SMLButton>
    </ModalFooter>
  );
};

export const GuideFooter = TypedMemo(GuideFooterComponent);
